import { useEffect, useState } from "react";
import { addError } from "@jsq/observability";

type Props = {
  pageId?: string;
  skip?: boolean;
};

type Incident = {
  id: string | null;
};

type StatusPageResponse = {
  page: {
    id: string;
    name: string;
    url: string;
    time_zone: string;
    updated_at: string;
  };
  incidents?: Incident[];
};

export const useGetUnresolvedIncidentIds = ({ pageId, skip = false }: Props) => {
  const [data, setData] = useState<string[] | null>(null);
  const [statusPageUrl, setStatusPageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!pageId || skip) {
      setData(null);
      setStatusPageUrl(null);
      return undefined;
    }

    const controller = new AbortController();

    const fetchIncidents = async () => {
      try {
        const response = await fetch(
          `https://${pageId}.statuspage.io/api/v2/incidents/unresolved.json`,
          { signal: controller.signal },
        );

        if (!response.ok) {
          throw new Error(`Response status: ${response.status}`);
        }

        const respData: StatusPageResponse = await response.json();
        const incidentIds =
          respData?.incidents
            ?.map((incident: Incident) => incident.id)
            ?.filter((id) => id !== null) ?? [];

        setData(incidentIds);
        setStatusPageUrl(respData?.page?.url ?? null);
      } catch (err) {
        if ((err as Error).name === "AbortError") {
          return;
        }
        addError(err, { context: { pageId } });
        setData(null);
        setStatusPageUrl(null);
      }
    };

    fetchIncidents();

    return () => {
      controller.abort();
    };
  }, [pageId, skip]);

  return { data, statusPageUrl };
};
